import { useState } from 'react';
import { signupFields } from '@/constants/FormFields';
import Button from '@/components/Button';
import Input from '@/components/Input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routes } from '@/constants/routes';

const fields = signupFields;
let fieldsState = {};

fields.forEach((field) => (fieldsState[field.id] = ''));

const Signup = ({ errors, setErrors }) => {
	const [signupState, setSignupState] = useState(fieldsState);
	const localStorageRole = JSON.parse(localStorage.getItem('role'));

	// If the user is signed in, then we set the role to nothing
	// Because the handleChange method will fill it as needed
	// Or else we send the user role
	const role = localStorageRole ? '' : 'user';

	const [validFields, setValidFields] = useState({ role: role && role !== '' });
	const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn'));
	const navigate = useNavigate();

	// Determine if all required fields are valid
	const allFieldsValid = () => fields.every((field) => !field.isRequired || validFields[field.id]);

	// Handle input change
	const handleChange = (e) => {
		const { id, type, value, required: isRequired } = e.target;

		let newErrors = { ...errors };
		let valid = true;

		// Required field check
		if (isRequired && !value) {
			newErrors[id] = `${id.charAt(0).toUpperCase() + id.slice(1)} is required.`;
			valid = false;
		} else {
			delete newErrors[id];
		}

		// Email validation
		if (type === 'email' && id === 'email') {
			const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
			if (!emailRegex.test(value)) {
				newErrors.email = 'Invalid email address.';
				valid = false;
			} else {
				delete newErrors.email;
			}
		}

		// Password validation - firstore default min: 6
		if (type === 'password' && id === 'password') {
			if (value.length < 6) {
				newErrors.password = 'Password must be at least 6 characters long.';
				valid = false;
			} else if (value.length > 30) {
				newErrors.password = 'Password must be less than 30 characters long.';
				valid = false;
			} else {
				delete newErrors.password;
			}
		}

		// Phone validation
		if (type === 'tel' && id === 'phone_number') {
			if (!/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(value)) {
				newErrors.phone_number = 'Invalid phone number';
				valid = false;
			} else {
				delete newErrors.phone_number;
			}
		}

		// Role validation
		if (type === 'text' && id === 'role') {
			if (value === '') {
				newErrors.role = 'Role is required';
				valid = false;
			} else {
				delete newErrors.role;
			}
		}

		// Update the state with the new errors and input value
		setErrors(newErrors);
		setSignupState({ ...signupState, [id]: value });

		// Update the validation status for this field
		setValidFields((prev) => ({ ...prev, [id]: valid }));
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		if (allFieldsValid()) {
			setSignupState({ ...signupState });

			try {
				await axios.post(`${import.meta.env.VITE_API_URL}/api/register`, {
					...signupState,
					role: isLoggedIn ? signupState.role : 'user',
				});

				toast.success('Votre profil a bien été créé', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				// We redirect the user the home page after 4 seconds
				setTimeout(() => navigate(routes.home), 1000);
			} catch (error) {
				console.error('Error registering user:', error);

				let errorMessage = 'Failed to register';
				if (error.message) {
					errorMessage = error.message; // Use the error message from the exception
				}

				toast.error(errorMessage, {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				// We refresh the page
				setTimeout(() => navigate(0), 1000);
			}
		}
	};

	return (
		<form onSubmit={handleSubmit} className='lg:w-2/5'>
			{fields.map((field, index) =>
				field.id !== 'role' ? (
					<Input
						key={field.id || index}
						handleChange={handleChange}
						value={signupState[field.id]}
						labelText={field.labelText}
						labelFor={field.labelFor}
						id={field.id}
						name={field.name}
						type={field.type}
						isRequired={field.isRequired}
						placeholder={field.placeholder}
						extraAttributes={field.type === 'password' ? { min: 2, max: 3 } : {}}
						fullWidthInput={true}
					/>
				) : (
					<>
						{localStorageRole && isLoggedIn && localStorageRole === 'super-admin' && (
							<select
								key={field.id}
								id={field.id}
								className="w-full border border-gray-300 rounded-md px-3 py-2"
								name={field.name}
								onChange={handleChange}
								value={signupState.role}
								required
							>
								<option value="">-- Role --</option>
								<option value="user">Utilisateur</option>
								<option value="admin">Administrateur</option>
								<option value="manager">Manager</option>
								{localStorageRole === 'super-admin' && <option value="super-admin">Super administrateur</option>}
							</select>
						)}
					</>
				)
			)}

			<Button handleSubmit={handleSubmit} text="Créer mon compte" disabled={allFieldsValid()} />
		</form>
	);
};

export default Signup;
